import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Globe, ArrowRight, X } from 'lucide-react';
import toast from 'react-hot-toast';
import BackButton from './BackButton';
import './URLOpener.css';

export default function URLOpener({ onClose }) {
  const [url, setUrl] = useState('');
  const [isValidUrl, setIsValidUrl] = useState(true);
  const navigate = useNavigate();

  // Check if input is a paste ID (alphanumeric, 4-20 chars)
  const isPasteId = (input) => {
    return /^[a-zA-Z0-9_-]{1,50}$/.test(input.trim());
  };

  const validateUrl = (inputUrl) => {
    try {
      // Add protocol if missing
      let fullUrl = inputUrl;
      if (inputUrl && !inputUrl.match(/^https?:\/\//)) {
        fullUrl = `https://${inputUrl}`;
      }
      
      new URL(fullUrl);
      return { isValid: true, url: fullUrl };
    } catch {
      return { isValid: false, url: inputUrl };
    }
  };

  const handleInputChange = (e) => {
    const inputUrl = e.target.value;
    setUrl(inputUrl);
    
    // Always valid if it could be a paste ID
    if (inputUrl.trim()) {
      if (isPasteId(inputUrl)) {
        setIsValidUrl(true);
      } else {
        const validation = validateUrl(inputUrl);
        setIsValidUrl(validation.isValid);
      }
    } else {
      setIsValidUrl(true);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!url.trim()) {
      toast.error('Please enter a paste ID or URL! 🌐', {
        style: {
          background: '#0f3460',
          color: '#eaeaea',
          border: '3px solid #ff6b6b',
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: '600'
        }
      });
      return;
    }

    const input = url.trim();

    // Check if it's a simple paste ID (like "abcd", "my-paste", etc.)
    if (isPasteId(input)) {
      toast.success(`Opening paste: ${input} 🚀`, {
        style: {
          background: '#0f3460',
          color: '#eaeaea',
          border: '3px solid #0eff9f',
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: '600'
        }
      });
      navigate(`/p/${input}`);
      setUrl('');
      if (onClose) onClose();
      return;
    }

    // Check if it's a path like /p/abcd
    if (input.startsWith('/p/')) {
      const pasteId = input.replace('/p/', '');
      toast.success(`Opening paste: ${pasteId} 🚀`, {
        style: {
          background: '#0f3460',
          color: '#eaeaea',
          border: '3px solid #0eff9f',
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: '600'
        }
      });
      navigate(`/p/${pasteId}`);
      setUrl('');
      if (onClose) onClose();
      return;
    }

    // Otherwise treat as external URL
    const validation = validateUrl(input);
    
    if (!validation.isValid) {
      toast.error('Invalid URL format! 🚨', {
        style: {
          background: '#0f3460',
          color: '#eaeaea',
          border: '3px solid #ff6b6b',
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: '600'
        }
      });
      return;
    }

    try {
      window.open(validation.url, '_blank', 'noopener,noreferrer');
      toast.success(`Opening ${validation.url} 🚀`, {
        style: {
          background: '#0f3460',
          color: '#eaeaea',
          border: '3px solid #0eff9f',
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: '600'
        }
      });
      
      // Clear input and close if callback provided
      setUrl('');
      if (onClose) onClose();
    } catch (error) {
      toast.error('Failed to open URL! 💥', {
        style: {
          background: '#0f3460',
          color: '#eaeaea',
          border: '3px solid #ff6b6b',
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: '600'
        }
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
    if (e.key === 'Escape' && onClose) {
      onClose();
    }
  };

  const quickLinks = [
    { name: 'New Paste', url: '/', icon: '✏️' },
    { name: 'Dashboard', url: '/dashboard', icon: '📄' },
    { name: 'Login', url: '/login', icon: '🔑' },
    { name: 'Published', url: '/p/example', icon: '🌐' },
  ];

  const handleQuickLink = (linkUrl) => {
    if (linkUrl.startsWith('/')) {
      // Internal route - use react router navigation
      navigate(linkUrl);
      toast.success(`Navigating to ${linkUrl} 🎯`, {
        style: {
          background: '#0f3460',
          color: '#eaeaea',
          border: '3px solid #0eff9f',
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: '600'
        }
      });
    } else {
      // External URL - open in new tab
      window.open(linkUrl, '_blank', 'noopener,noreferrer');
      toast.success(`Opening ${linkUrl} 🚀`, {
        style: {
          background: '#0f3460',
          color: '#eaeaea',
          border: '3px solid #0eff9f',
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: '600'
        }
      });
    }
  };

  return (
    <div className="url-opener-page">
      <BackButton label="Back" />
      <div className="url-opener">
        <div className="url-opener-header">
          <div className="url-opener-title">
            <Globe size={20} />
            <h3>URL OPENER</h3>
          </div>
          {onClose && (
            <button onClick={onClose} className="url-opener-close">
              <X size={18} />
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="url-opener-form">
          <div className="url-input-group">
            <input
              type="text"
              value={url}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Enter paste ID or URL (e.g., abc123, /p/abc123)"
              className={`url-input ${!isValidUrl ? 'invalid' : ''}`}
              autoFocus
            />
            <button type="submit" className="url-submit-btn" disabled={!url.trim()}>
              <ExternalLink size={16} />
              OPEN
            </button>
          </div>
          
          {!isValidUrl && (
            <p className="url-error">
              ⚠️ Invalid URL format. Try: abc123, /p/abc123, or full URLs
            </p>
          )}
        </form>

        <div className="quick-links">
          <h4>QUICK LINKS</h4>
          <div className="quick-links-grid">
            {quickLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleQuickLink(link.url)}
                className="quick-link-btn"
                title={`Open ${link.name}`}
              >
                <span className="quick-link-icon">{link.icon}</span>
                <span className="quick-link-name">{link.name}</span>
                <ArrowRight size={14} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}